// Analytics product init — real implementation (iteration 02).
//
// Constructs and starts the @raventic/sdk-analytics-v1 engine PROGRAMMATICALLY
// from its library exports, mirroring the SDK's own src/index.manual.ts assembly
// (verified against D:\Sassik\sdk-analytics). No CDN loader is injected: the
// facade is bundled into the customer's widget.js, so there is no separate script.
//
// IMPORTANT (build wiring, iteration 02 / step 05): BeaconSender reads the
// runtime constant `process.env.RAVENTIC_ANALYTICS_BASE_API_URL` at construction
// (BeaconSender.getBaseApiUrl throws if it is undefined) and `.replace()`s the
// literal `__REGION__` token in it with the apiKey's region at send time. The
// customer Vite build MUST `define` it — the actual wiring lands in step 05:
//   define: {
//     "process.env.RAVENTIC_ANALYTICS_BASE_API_URL":
//       JSON.stringify("https://__REGION__.api.raventic.ai"),
//   }
//
// Importing the package has a side effect: it installs `window.raventicLayer` and
// the `window.RaventicAnalytics` push shim at import time, so events pushed before
// a deferred start() are queued (not lost) and drained on start(). We rely on that
// shim and do NOT re-implement it.
//
// A/B: bucketing lives in ../ab.ts (iteration 18 config experiments, which replaced
// iteration 10's `abTest`); boot() resolves the arm once and passes its id in as
// initAnalytics()'s `variant` param, which becomes the RaventicAnalytics ctor arg.
//
// TIMING (iteration 20, consent fix after review): boot() calls this at widget.js execution,
// BEFORE the DOM wait, and start() runs synchronously (no idle-callback deferral any more;
// `lazy` is deprecated and ignored): start() registers one timer and the `visibilitychange` /
// `pagehide` flush listeners, which exist only once it ran — a deferred start lost the events of
// visitors who left early. But start() ALSO calls Identify() at once, and identifying that early
// would read consent before the page's own consent signals exist: precedence.ts reads
// `window.RaventicConsent` per call and falls back to granted, so a shop setting
// `RaventicConsent = false` in a later <head> script (or a CMP detector that only answers once
// its banner script has loaded) would see `rvn_c` written / `rvn_p` incremented before its
// denial existed, and the init event's page number frozen from that first identification. So the
// identifier the engine gets is wrapped in AfterReadyIdentifier: Identify() first awaits `ready`
// (boot's DOM readiness — the same whenDomReady() promise the widgets wait for), then runs the
// consent-aware identification. Consent is therefore read at the same moment as before this
// track (DOM readiness), minus the old idle-callback delay, and nothing identifies or touches
// storage before it. Verified against sdk-analytics 1.16.0 (src/analytics/analytics.ts): an
// unload flush BEFORE DOM readiness has no `lastIdentity` and sends under the fixed
// `AnonymousClient` with page 1, writing nothing (processEventsSync never calls the identifier);
// the periodic send's processEvents() awaits Identify(), i.e. the wait; the collectors and
// BeaconSender touch no storage; the library entry runs no A/B code (src/ab.ts is a loader-only
// include). `activateWhen` still runs at widget.js execution, before the document is parsed:
// URL only.

import {
  RaventicAnalytics,
  BeaconSender,
  ConsentAwareIdentifierWrapper,
  LocalStorageClientIdentifier,
  RaventicLayerCollector,
  DataLayerCollector,
  setDebug,
} from "@raventic/sdk-analytics-v1";
import type { AnalyticsActivation, ConsentDetection } from "../config";
import type { ActivationContext } from "../activation";
import { resolvePredicate } from "../activation";
import { withRaventicConsent } from "../consent/precedence";

// sdk-analytics' ClientIdentifier / Identity, structurally (the library barrel doesn't export the
// interface types by these names).
interface Identity {
  client: string;
  page: number;
}
interface ClientIdentifier {
  Identify(): Promise<Identity>;
}

// Defers identification until `ready` (see TIMING in the header): no consent read, no storage
// access, no identity before it. Every later call goes straight to the wrapped identifier.
export class AfterReadyIdentifier implements ClientIdentifier {
  private readonly ready: Promise<unknown>;
  private readonly wrapped: ClientIdentifier;

  public constructor(ready: Promise<unknown>, wrapped: ClientIdentifier) {
    this.ready = ready;
    this.wrapped = wrapped;
  }

  public async Identify(): Promise<Identity> {
    await this.ready;
    return this.wrapped.Identify();
  }
}

// The analytics package augments Window (dataLayer / raventicVariant) via its own
// global.d.ts. We reach those props through a narrow local view so this file also
// typechecks offline, where the dep — and its augmentation — is absent.
interface AnalyticsWindow {
  dataLayer?: unknown[];
  raventicVariant?: string | null;
  RaventicConsent?: unknown; // typed and resolved in ../consent/precedence.ts
}

// The apiKey must be 3 region letters followed by hex; regionFromKey/BeaconSender
// throw otherwise (the region is derived from the first 3 chars).
const API_KEY_PATTERN = /^[a-z]{3}[a-f0-9]+$/;

export function initAnalytics(
  cfg: AnalyticsActivation,
  ctx: ActivationContext,
  variant: string | null,
  // When identification may start (boot: its DOM-readiness promise). Default: at once.
  ready: Promise<unknown> = Promise.resolve(),
): RaventicAnalytics | undefined {
  // Analytics is on by default on every page; a predicate can still gate it.
  if (!resolvePredicate(cfg.activateWhen, ctx, true)) return;

  // Guard the apiKey up front so a bad key fails here (recorded cleanly by boot()'s
  // try/catch) rather than throwing later mid send-cycle inside BeaconSender.
  if (!API_KEY_PATTERN.test(cfg.apiKey)) {
    throw new Error(
      "[@raventic/sdk] analytics: invalid apiKey (expected 3 region letters + hex)",
    );
  }

  // Info logging (sdk-analytics-v1 1.15.6 setDebug; module-global, off by default in
  // its library build): explicit config wins, else on only outside production builds
  // (`process.env.NODE_ENV` is baked by the customer build's define map).
  setDebug(cfg.debug ?? process.env.NODE_ENV !== "production");

  const w = window as unknown as AnalyticsWindow;
  const useDataLayer = cfg.useDataLayer !== false;

  // A/B: the variant is resolved ONCE by boot() (see ../ab.ts) and passed in; this
  // module does no bucketing of its own. null for a plain (non-experiment) config or a
  // broken experiment (arm 0 unrecorded), in which case the init event omits the field.
  // Assigned here too (mirroring index.manual.ts, which always sets it) so the global is
  // null, not stale, when off.
  w.raventicVariant = variant;

  if (useDataLayer) {
    w.dataLayer = w.dataLayer || [];
  }

  // Consent precedence (../consent/precedence.ts), re-evaluated before every send:
  // window.RaventicConsent when the page sets it (boolean, or a function called with
  // 'analytics_storage') ALWAYS wins — the shop's/CMP's explicit signal, as in the SDK's
  // manual-path resolver; else the site-specific `cfg.consent` detector (e.g. one from
  // `@raventic/sdk/consent`); else granted. Throws reach ConsentAwareIdentifierWrapper,
  // which treats them as denied (anonymous events).
  const consent: ConsentDetection = withRaventicConsent(w, cfg.consent);

  // RaventicLayerCollector MUST be first; DataLayerCollector is appended unless opted out.
  const collectors = useDataLayer
    ? [new RaventicLayerCollector(), new DataLayerCollector()]
    : [new RaventicLayerCollector()];

  const engine = new RaventicAnalytics(
    cfg.apiKey,
    variant,
    new BeaconSender(),
    new AfterReadyIdentifier(ready, new ConsentAwareIdentifierWrapper(consent, new LocalStorageClientIdentifier())),
    ...collectors,
  );

  // Started synchronously, right away (see TIMING in the header): only start() registers
  // the send timer + the unload-flush listeners; its Identify() waits for `ready`.
  // `cfg.lazy` is deprecated and ignored.
  engine.start();

  return engine;
}
